import classNames from "classnames";
import React, { useState } from "react";
import { t } from "ttag";

import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { Form } from "../../../forms/Form";
import { FormCheckbox } from "../../../forms/FormCheckbox";
import { FormSubmit } from "../../../forms/FormSubmit";
import { NonFieldErrors } from "../../../forms/NonFieldErrors";
import { strings } from "../../../localization";
import { IBasket } from "../../../models/catalogue";
import { IBasketLineAPIURL } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { BasketLineMinimal } from "../BasketLine";
import { ExpeditedShippingDisclosure } from "../components/ExpeditedShippingDisclosure";
import { PredictedDeliveryHelpText } from "../components/PredictedDeliveryHelpText";
import { PreferredDeliveryDate } from "../components/PreferredDeliveryDate";
import { ShippingNotes } from "../components/ShippingNotes";
import {
    PredictedDeliveryDateDisplayType,
    PreferredDeliveryDateDisplayType,
    SHIPPING_METHOD_NAMES,
    Step,
} from "../constants";
import { setFields } from "../reducers";
import { IReduxFormState } from "../reducers.interfaces";
import {
    ILineOptionGroupingData,
    IMethodDetails,
    getLineOptionGroupingData,
} from "../selectors";
import { CheckoutStep } from "./CheckoutStepContainer";
import { ShippingMethodSummary } from "./ShippingMethodSummary";

import styles from "./ShippingMethod.module.scss";

interface ShippingMethodProps {
    heading: string;
    errors: string[];
    predictedDeliveryDates: PredictedDeliveryDateDisplayType;
    preferredDeliveryDates: PreferredDeliveryDateDisplayType;
    isCurrentStep: boolean;
    saveShippingMethod: () => Promise<IBasket>;
    savePreferredDeliveryDate: (date: Date | null) => Promise<void>;
    onContinue: (event?: React.MouseEvent<HTMLElement>) => void;
    onEdit: (event?: React.MouseEvent<HTMLElement>) => void;
    preferredDeliveryContent?: string | React.ReactNode;
    formRef?: React.RefObject<HTMLFormElement | null>;
}

function buildLine(
    basket: IBasket | null,
    lineURL: IBasketLineAPIURL,
): React.ReactNode {
    if (!basket) {
        return null;
    }
    const line = basket.lines.find((l) => {
        return l.url === lineURL;
    });
    if (!line || !line.quantity) {
        return null;
    }
    // Don't show bundle lines (any line which has children)
    const children = basket.lines.filter((l) => l.parent === line.id);
    if (children.length) {
        return;
    }
    const isBundled =
        basket.lines.filter((l) => l.id === line.parent).length > 0;

    return (
        <BasketLineMinimal
            key={`${lineURL}`}
            line={line}
            isBundled={isBundled}
        />
    );
}

function buildOptionSelector(
    groupIndex: number,
    methods: IMethodDetails[],
    form: IReduxFormState,
    changeFormField: (fields: Partial<IReduxFormState>) => void,
    predictedDeliveryDates: PredictedDeliveryDateDisplayType,
    preferredDeliveryDates: PreferredDeliveryDateDisplayType,
    savePreferredDeliveryDate: (date: Date | null) => Promise<void>,
    preferredDeliveryContent?: string | React.ReactNode,
): React.ReactNode[] {
    return methods.map((method) => {
        const isChecked = form.shipping_method === method.code;
        const isWhiteGlove = method.type === "freight";
        const setShippingMethod = () => {
            changeFormField({
                shipping_method: method.code,
            });
        };

        return (
            <React.Fragment key={method.code}>
                {/*
                    Render a FormCheckbox without helpText
                    for White Glove Delivery
                */}
                {isWhiteGlove ? (
                    <>
                        {/* TODO(i18n): Keep complete sentence together with interpolation: t`${methodName} - ${method.price}` */}
                        <FormCheckbox
                            type="radio"
                            id={`shipping_method_${groupIndex}`}
                            name={`shipping_method_${groupIndex}`}
                            value={method.code}
                            disabled={methods.length === 1}
                            checked={methods.length === 1 || isChecked}
                            label={
                                (SHIPPING_METHOD_NAMES[method.name] ||
                                    method.name) +
                                " - " +
                                method.price
                            }
                            className={styles.radio}
                            helpTextClass={`form__help-text--shipping-method-${groupIndex}`}
                            onChange={setShippingMethod}
                        />
                        {preferredDeliveryDates ===
                        PreferredDeliveryDateDisplayType.DISABLED ? (
                            <PredictedDeliveryHelpText
                                type={predictedDeliveryDates}
                                additionalHelpText={method.description}
                            />
                        ) : (
                            <PreferredDeliveryDate
                                type={preferredDeliveryDates}
                                savePreferredDeliveryDate={
                                    savePreferredDeliveryDate
                                }
                                fallbackDescription={method.description}
                                preferredDeliveryContent={
                                    preferredDeliveryContent
                                }
                            />
                        )}
                    </>
                ) : (
                    <FormCheckbox
                        type="radio"
                        id={`shipping_method_${groupIndex}`}
                        name={`shipping_method_${groupIndex}`}
                        value={method.code}
                        disabled={methods.length === 1}
                        checked={methods.length === 1 || isChecked}
                        // TODO(i18n): Keep complete sentence together with interpolation: t`${methodName} - ${method.price}`
                        label={
                            (SHIPPING_METHOD_NAMES[method.name] ||
                                method.name) +
                            " - " +
                            method.price
                        }
                        className={styles.radio}
                        helpText={method.description}
                        helpTextClass={`form__help-text--shipping-method-${groupIndex}`}
                        onChange={setShippingMethod}
                    />
                )}
            </React.Fragment>
        );
    });
}

function buildLineOptionGroupings(
    showForm: boolean,
    groups: ILineOptionGroupingData[],
    basket: IBasket | null,
    form: IReduxFormState,
    changeFormField: (fields: Partial<IReduxFormState>) => void,
    predictedDeliveryDates: PredictedDeliveryDateDisplayType,
    preferredDeliveryDates: PreferredDeliveryDateDisplayType,
    savePreferredDeliveryDate: (date: Date | null) => Promise<void>,
    preferredDeliveryContent?: string | React.ReactNode,
): React.ReactNode {
    if (!showForm) {
        return null;
    }
    return groups.map((group, i) => {
        const lines = group.lines.map((line) => {
            return buildLine(basket, line);
        });
        const options = buildOptionSelector(
            i,
            group.methods,
            form,
            changeFormField,
            predictedDeliveryDates,
            preferredDeliveryDates,
            savePreferredDeliveryDate,
            preferredDeliveryContent,
        );
        return (
            <div key={i} className="checkout-step__shipping-group">
                <h3 className="checkout-step__subheading">{t`ITEM(S):`}</h3>
                <ul>{lines}</ul>
                <h3 className="checkout-step__subheading">
                    {t`SHIPPING METHOD:`}
                </h3>
                <div>
                    <fieldset className={styles.methods}>
                        <legend className="ada-screenreader-only">
                            {t`Choose a shipping method`}
                        </legend>
                        {options}
                    </fieldset>
                </div>
            </div>
        );
    });
}

function basketContainsWhiteGlove(groups: ILineOptionGroupingData[]): boolean {
    for (const group of groups) {
        for (const method of group.methods) {
            if (method.type === "freight") {
                return true;
            }
        }
    }
    return false;
}

export const ShippingMethod = (props: ShippingMethodProps): React.ReactNode => {
    const [isSaving, setIsSaving] = useState(false);

    const form = useAppSelector((state) => state.checkout.form);
    const basket = useAppSelector((state) => state.checkout.data.basket);
    const groups = useAppSelector((state) =>
        getLineOptionGroupingData(state.checkout),
    );
    const showForm = useAppSelector(
        (state) => state.checkout.form.current_step === Step.SHIPPING_METHOD,
    );
    const dispatch = useAppDispatch();

    const changeFormField = (fields: Partial<IReduxFormState>) => {
        dispatch(setFields(fields));
    };

    const handleSaveShippingMethod = async () => {
        setIsSaving(true);
        await props.saveShippingMethod();
        setIsSaving(false);
        props.onContinue();
    };

    const handleSubmit = (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        return handleSaveShippingMethod();
    };

    const bodyClasses = classNames({
        "checkout-step__body": true,
        "checkout-step__body--collapsed": !showForm,
    });

    let bodyContent: React.ReactNode;
    if (groups.length > 0) {
        bodyContent = (
            <div className={bodyClasses}>
                <Form onSubmit={handleSubmit} ref={props.formRef}>
                    <NonFieldErrors
                        errors={props.errors}
                        showErrorMessages={true}
                    />
                    {buildLineOptionGroupings(
                        showForm,
                        groups,
                        basket,
                        form,
                        changeFormField,
                        props.predictedDeliveryDates,
                        props.preferredDeliveryDates,
                        props.savePreferredDeliveryDate,
                        props.preferredDeliveryContent,
                    )}
                    <ExpeditedShippingDisclosure groups={groups} />
                    {basketContainsWhiteGlove(groups) && (
                        <ShippingNotes
                            value={form.shipping_notes}
                            changeFormField={changeFormField}
                            disabled={isSaving}
                        />
                    )}
                    {/* TODO(i18n): Replace strings.get with t tagged template: t`Continue` */}
                    <FormSubmit
                        className="button button--full-width button--continue-to-billing"
                        value={
                            strings.get("CONTINUE_BTN_LABEL_SHIPPING_METHOD") ||
                            "Continue"
                        }
                        disabled={isSaving}
                    />
                </Form>
            </div>
        );
    } else {
        bodyContent = (
            <div className={bodyClasses}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <CheckoutStep
            className="checkout-step--shipping-method form"
            heading={props.heading}
            isCurrentStep={props.isCurrentStep}
        >
            {bodyContent}
            <ShippingMethodSummary onEdit={props.onEdit} />
        </CheckoutStep>
    );
};
